import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { Link, useParams } from "react-router-dom";
import SeoHead from "@/seo/SeoHead";
import { buildBlogPostingJsonLd, buildBreadcrumbJsonLd } from "@/seo/jsonLd";
import { useSiteContent } from "@/cms/SiteContentContext";
import { editableAttrs } from "@/cms/previewUtils";

export default function BlogDetayPage() {
  const { slug } = useParams();
  const { content } = useSiteContent();
  const section = content.blog;
  const post = section.posts.find((item) => item.slug === slug);
  const index = section.posts.findIndex((item) => item.slug === slug);

  if (!post) {
    return (
      <div className="min-h-screen bg-zinc-950">
        <SeoHead
          title={section.detail.notFoundTitle}
          description="Aradığınız blog yazısına ulaşılamadı."
          path="/blog"
          noindex
        />
        <Navbar />
        <section className="pt-36 pb-24">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h1 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: "Rajdhani, sans-serif" }}>
              {section.detail.notFoundTitle}
            </h1>
            <Link to="/blog" className="text-red-500 hover:text-red-400 font-semibold">
              {section.detail.notFoundActionLabel}
            </Link>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  const pagePath = `/blog/${post.slug}`;
  const jsonLd = [
    buildBreadcrumbJsonLd([
      { name: "Ana Sayfa", path: "/" },
      { name: "Blog", path: "/blog" },
      { name: post.title, path: pagePath },
    ]),
    buildBlogPostingJsonLd({
      title: post.title,
      description: post.excerpt,
      path: pagePath,
      image: post.image,
      datePublished: post.publishedAt,
    }),
  ];

  return (
    <div className="min-h-screen bg-zinc-950">
      <SeoHead title={post.title} description={post.excerpt} path={pagePath} image={post.image} type="article" jsonLd={jsonLd} />
      <Navbar />

      <section className="relative pt-36 pb-16 border-b border-zinc-800/70 overflow-hidden" {...editableAttrs(`blog.posts.${index}`, "object")}>
        <div className="absolute inset-0 z-0 bg-gradient-to-r from-zinc-950 via-[#130b0b] to-zinc-950" />
        <div className="relative z-10 max-w-5xl mx-auto px-6">
          <div className="flex items-center gap-2 mb-4 text-sm">
            <Link to="/" className="text-zinc-500 hover:text-red-500 transition-colors">
              Ana Sayfa
            </Link>
            <span className="text-zinc-700">/</span>
            <Link to="/blog" className="text-zinc-500 hover:text-red-500 transition-colors">
              Blog
            </Link>
            <span className="text-zinc-700">/</span>
            <span className="text-red-500" {...editableAttrs(`blog.posts.${index}.title`, "text")}>
              {post.title}
            </span>
          </div>
          <span className="text-red-500 text-xs font-semibold tracking-widest uppercase" {...editableAttrs(`blog.posts.${index}.category`, "text")}>
            {post.category}
          </span>
          <h1 className="text-4xl md:text-6xl font-bold text-white mt-3 leading-tight" style={{ fontFamily: "Rajdhani, sans-serif" }} {...editableAttrs(`blog.posts.${index}.title`, "text")}>
            {post.title}
          </h1>
          <div className="flex items-center gap-3 text-sm text-zinc-400 mt-5">
            <span {...editableAttrs(`blog.posts.${index}.date`, "text")}>{post.date}</span>
            <span>•</span>
            <span {...editableAttrs(`blog.posts.${index}.readTime`, "text")}>
              {post.readTime} <span {...editableAttrs("blog.detail.readSuffix", "text")}>{section.detail.readSuffix}</span>
            </span>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="max-w-5xl mx-auto px-6">
          <div className="overflow-hidden border border-zinc-800 mb-8">
            <img
              src={post.image}
              alt={post.title}
              className="w-full h-[260px] md:h-[420px] object-cover"
              {...editableAttrs(`blog.posts.${index}.image`, "image")}
            />
          </div>
          <div className="bg-zinc-900 border border-zinc-800 p-6 md:p-10 space-y-6">
            <h2 className="text-2xl md:text-3xl text-white font-bold" style={{ fontFamily: "Rajdhani, sans-serif" }} {...editableAttrs("blog.detail.overviewTitle", "text")}>
              {section.detail.overviewTitle}
            </h2>
            <div className="space-y-4" {...editableAttrs(`blog.posts.${index}.content`, "array")}>
              {post.content.map((paragraph, paragraphIndex) => (
                <p key={`content-${paragraphIndex}`} className="text-zinc-300 leading-relaxed" {...editableAttrs(`blog.posts.${index}.content.${paragraphIndex}`, "textarea")}>
                  {paragraph}
                </p>
              ))}
            </div>

            <div className="pt-4 border-t border-zinc-800" {...editableAttrs(`blog.posts.${index}.highlights`, "array")}>
              <h3 className="text-xl md:text-2xl text-white font-bold mb-4" style={{ fontFamily: "Rajdhani, sans-serif" }} {...editableAttrs("blog.detail.highlightsTitle", "text")}>
                {section.detail.highlightsTitle}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {post.highlights.map((item, highlightIndex) => (
                  <div key={`highlight-${highlightIndex}`} className="flex items-start gap-3 bg-zinc-950 border border-zinc-800 p-4">
                    <i className="ri-check-double-line text-red-500 mt-0.5"></i>
                    <span className="text-zinc-300 text-sm leading-relaxed" {...editableAttrs(`blog.posts.${index}.highlights.${highlightIndex}`, "text")}>
                      {item}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="pt-4 border-t border-zinc-800" {...editableAttrs(`blog.posts.${index}.faqs`, "array")}>
              <h3 className="text-xl md:text-2xl text-white font-bold mb-4" style={{ fontFamily: "Rajdhani, sans-serif" }} {...editableAttrs("blog.detail.faqTitle", "text")}>
                {section.detail.faqTitle}
              </h3>
              <div className="space-y-3">
                {post.faqs.map((faq, faqIndex) => (
                  <div key={`faq-${faqIndex}`} className="bg-zinc-950 border border-zinc-800 p-4">
                    <p className="text-white font-semibold mb-2" {...editableAttrs(`blog.posts.${index}.faqs.${faqIndex}.q`, "text")}>
                      {faq.q}
                    </p>
                    <p className="text-zinc-400 text-sm leading-relaxed" {...editableAttrs(`blog.posts.${index}.faqs.${faqIndex}.a`, "textarea")}>
                      {faq.a}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="mt-8">
            <Link to="/blog" className="inline-flex items-center gap-2 text-red-500 hover:text-red-400 font-semibold">
              <i className="ri-arrow-left-line"></i>
              <span {...editableAttrs("blog.detail.backToListLabel", "text")}>{section.detail.backToListLabel}</span>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
